import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for subscriptions */
export type SubscriptionsId = string & { __flavor: "SubscriptionsId" };

/**
 * Represents the table public.subscriptions
 * @uniqueConstraints {"subscriptions_pkey":{"name":"subscriptions_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]}}
 */
export default interface SubscriptionsTable {
  id: ColumnType<SubscriptionsId, SubscriptionsId, SubscriptionsId>;

  plan: ColumnType<string, string, string>;

  referenceId: ColumnType<string, string, string>;

  stripeCustomerId: ColumnType<string | null, string | null, string | null>;

  stripeSubscriptionId: ColumnType<string | null, string | null, string | null>;

  status: ColumnType<string | null, string | null, string | null>;

  periodStart: ColumnType<Date | null, Date | string | null, Date | string | null>;

  periodEnd: ColumnType<Date | null, Date | string | null, Date | string | null>;

  trialStart: ColumnType<Date | null, Date | string | null, Date | string | null>;

  trialEnd: ColumnType<Date | null, Date | string | null, Date | string | null>;

  cancelAtPeriodEnd: ColumnType<boolean | null, boolean | null, boolean | null>;

  seats: ColumnType<number | null, number | null, number | null>;
}

export type Subscriptions = Selectable<SubscriptionsTable>;

export type NewSubscriptions = Insertable<SubscriptionsTable>;

export type SubscriptionsUpdate = Updateable<SubscriptionsTable>;

export const subscriptionsId = z.string() as unknown as z.Schema<SubscriptionsId>;

export const subscriptions = z.object({
  id: subscriptionsId,
  plan: z.string(),
  referenceId: z.string(),
  stripeCustomerId: z.string().nullable(),
  stripeSubscriptionId: z.string().nullable(),
  status: z.string().nullable(),
  periodStart: z.date().nullable(),
  periodEnd: z.date().nullable(),
  trialStart: z.date().nullable(),
  trialEnd: z.date().nullable(),
  cancelAtPeriodEnd: z.boolean().nullable(),
  seats: z.number().nullable(),
});

export const subscriptionsInitializer = z.object({
  id: subscriptionsId,
  plan: z.string(),
  referenceId: z.string(),
  stripeCustomerId: z.string().optional().nullable(),
  stripeSubscriptionId: z.string().optional().nullable(),
  status: z.string().optional().nullable(),
  periodStart: z.date().optional().nullable(),
  periodEnd: z.date().optional().nullable(),
  trialStart: z.date().optional().nullable(),
  trialEnd: z.date().optional().nullable(),
  cancelAtPeriodEnd: z.boolean().optional().nullable(),
  seats: z.number().optional().nullable(),
});

export const subscriptionsMutator = z.object({
  id: subscriptionsId.optional(),
  plan: z.string().optional(),
  referenceId: z.string().optional(),
  stripeCustomerId: z.string().optional().nullable(),
  stripeSubscriptionId: z.string().optional().nullable(),
  status: z.string().optional().nullable(),
  periodStart: z.date().optional().nullable(),
  periodEnd: z.date().optional().nullable(),
  trialStart: z.date().optional().nullable(),
  trialEnd: z.date().optional().nullable(),
  cancelAtPeriodEnd: z.boolean().optional().nullable(),
  seats: z.number().optional().nullable(),
});

// ── constraints for Subscriptions ─────────────────────────────────
export const SubscriptionsConstraints = {
  subscriptions_pkey: {
    name: "subscriptions_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
} as const;
export type SubscriptionsConstraintsType = typeof SubscriptionsConstraints;
